import type { ReactNode } from "react";
import { Link } from "react-router";

import { Icon } from "./Icon";
import type { IconName } from "./icons/paths";
import { RoundIcon, type RoundIconTone } from "./RoundIcon";

/**
 * Строка меню профиля — `.setting-row` из пакета дизайна.
 *
 * Иконка в круге со свечением, а не в залитом квадрате: в макете тон круга
 * различает разделы (уведомления и язык — голубые, реферальная программа —
 * фиолетовая), и это единственное, что отличает строки друг от друга кроме
 * подписи.
 *
 * Движение (FE-13): нажатие сжимает строку на процент (`press-row` — крупный
 * блок с масштабом кнопки выглядел бы резиновым), каретка при этом уезжает
 * вправо, показывая направление перехода.
 */
interface MenuRowProps {
  to: string;
  icon: IconName;
  title: string;
  subtitle?: string;
  /** Тон круга иконки. */
  tone?: RoundIconTone;
  /** Пилюля рядом с заголовком — «Бонусы» у реферальной программы. */
  badge?: ReactNode;
  trailing?: ReactNode;
}

export function MenuRow({
  to,
  icon,
  title,
  subtitle,
  tone = "cyan",
  badge,
  trailing,
}: MenuRowProps) {
  return (
    <Link
      to={to}
      className="press-row panel-surface group flex min-h-16 items-center gap-3 rounded-lg px-3.5 py-3"
    >
      <RoundIcon icon={icon} size={38} tone={tone} />

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Заголовок переносится, а не обрезается: «Реферальная программа» в
            одну строку рядом с бейджем не помещается, а «Реферальная програм…»
            — это уже не название раздела. */}
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-body font-medium text-text-primary">{title}</span>
          {badge}
        </span>
        {subtitle ? (
          <span className="truncate text-caption font-normal text-text-secondary">{subtitle}</span>
        ) : null}
      </span>

      {trailing}

      <Icon
        name="caretRight"
        size={16}
        className="shrink-0 text-text-tertiary transition-transform duration-[var(--motion-fast)] ease-out-quint group-active:translate-x-0.5"
      />
    </Link>
  );
}
